import {AlertIcon} from '@primer/octicons-react'
import type {UITask} from '../../types/task'
import {SimpleListView} from '@github-ui/simple-list-view'
import {SimpleListHeader} from '@github-ui/simple-list-view/SimpleListHeader'
import {SimpleListItem} from '@github-ui/simple-list-view/SimpleListItem'
import {ListItemsSkeletonLoader} from './ListParts'
import {Blankslate, SkeletonText} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import {ListPagination} from '../ListPagination'

import styles from './TaskListLayout.module.css'

export interface TaskListLayoutProps {
  tasks: UITask[]
  isError: boolean
  isLoading: boolean
  showSkeletonLoaders: boolean
  hasNextPage: boolean
  header: ReactNode
  /** Content pinned opposite `header` in the space-between header row. */
  headerTrailing?: ReactNode
  emptyHeading: string
  emptyDescription: ReactNode
  errorText: string
  renderItem: (task: UITask) => ReactNode
  /**
   * Header spacing density. `'compact'` reduces the header's vertical padding so
   * the header sits closer to preceding content (used by the redesigned agents view).
   * Defaults to `'default'`.
   */
  density?: 'default' | 'compact'
  /** Keep the standard horizontal inset for plain headers, rather than tightening it on mobile for tabs. */
  headerPadding?: 'responsive' | 'normal'
}

/**
 * Shared structural component for task list views.
 * Handles layout, error/empty/skeleton states, and pagination.
 * Each list variant supplies its own header content and item renderer.
 */
export function TaskListLayout({
  tasks,
  isError,
  isLoading,
  showSkeletonLoaders,
  hasNextPage,
  header,
  headerTrailing,
  emptyHeading,
  emptyDescription,
  errorText,
  renderItem,
  density = 'default',
  headerPadding = 'responsive',
}: TaskListLayoutProps) {
  return (
    <div className={styles.list}>
      <SimpleListView>
        <SimpleListHeader
          className={clsx(
            styles.header,
            density === 'compact' && styles.headerCompact,
            headerPadding === 'normal' && styles.headerNormalPadding,
          )}
        >
          <SimpleListHeader.Metadata>{header}</SimpleListHeader.Metadata>
          {headerTrailing}
        </SimpleListHeader>
        {tasks.length === 0 && !isError && !isLoading ? (
          <Blankslate className={styles.listContainer}>
            {showSkeletonLoaders ? (
              <>
                <SkeletonText size="titleMedium" />
                <SkeletonText size="bodyMedium" lines={2} />
              </>
            ) : (
              <>
                <Blankslate.Heading>{emptyHeading}</Blankslate.Heading>
                <Blankslate.Description>{emptyDescription}</Blankslate.Description>
              </>
            )}
          </Blankslate>
        ) : (
          <SimpleListView.Items className={clsx(styles.listContainer, styles.items)}>
            {isError ? (
              <SimpleListItem className={styles.emptyItem}>
                <AlertIcon className={styles.noItemsIcon} />
                <p className={styles.emptyTitle}>{errorText}</p>
              </SimpleListItem>
            ) : showSkeletonLoaders ? (
              <ListItemsSkeletonLoader />
            ) : (
              <>{tasks.map(task => renderItem(task))}</>
            )}
          </SimpleListView.Items>
        )}
      </SimpleListView>
      <ListPagination hasNextPage={hasNextPage} hidden={showSkeletonLoaders} />
    </div>
  )
}
